import { ReactElement, useState } from 'react';
import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  Row,
  Spin,
  Tabs,
  TabsProps,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { PermissionType } from '@cbinsights/adminpermissionservice/adminpermissionservice';
import EditableText from 'client/component-library/EditableText/EditableText';
import { useTypedFlags } from 'client/component-library/FeatureFlags/hooks/useTypedFlags';
import { dataFeedPermissions } from 'client/modules/data-feed-credit/permissions';
import { externalApiPermissions } from 'client/modules/external-api/permissions';
import FeatureUsage from 'client/modules/team/components/FeatureUsage';
import { LEGACY_SITE } from 'client/modules/users/constants';
import useHasEditAccess from 'client/modules/users/hooks/useHasEditAccess';
import useHasAccess from 'client/utils/permissions-validation/hooks/useHasAccess';
import { TeamRoutes, itemRender } from '../../RouteHelpers';
import AddExistingUser from './AddExistingUsers';
import DataFeedCredit from '../DataFeedCredit';
import DataFeedCreditV2 from '../DataFeedsV2';
import ExternalApiClients from '../ExternalApiClients';
import FormerTeamMembers from './FormerTeamMembers';
import MCPSettings from '../MCPSettings';
import PackageSetting from './PackageSetting';
import { PauseSubscriptionModal } from '../PendingRenewal/PauseSubscriptionModal';
import PendingInvitations from './PendingInvitations';
import { PendingRenewalBanner } from '../PendingRenewal/PendingRenewalBanner';
import { SFDCInfo } from './SFDCInfo';
import SFOportunity from '../SalesforceAccount/SFOpportunity';
import SSODetails from '../SSODetails';
import TeamMembers from './TeamMembers';
import { TeamSearch } from '../TeamSearch';
import { TeamSettings } from './TeamSettings';
import TeamUpdates from '../TeamUpdates';
import { UnpauseSubscriptionModal } from '../PendingRenewal/UnpauseSubscriptionModal';
import { usePendingRenewal } from '../../hooks/usePendingRenewal';
import useSelectedTab from '../../hooks/useSelectedTab';
import useTeamForm from '../../hooks/useTeamForm';
import useTeamInvitations from '../../hooks/useTeamInvitations';
import { useTeamMembers } from '../../hooks/useTeamMembers';
import { useUpdateTeam } from '../../hooks/useUpdateTeam';
import { EXPIRED_PACKAGE } from '../../utils/package';

export const TeamPage = (): ReactElement => {
  const { initialValues, idTeam, refetchGetTeam, isLoading } = useTeamForm();

  const {
    columns,
    formerColumns,
    data,
    formerData,
    refetchGetTeamMembers,
    setTeamMemberFilter,
    isLoadingTeamMembers,
  } = useTeamMembers(initialValues.teamId);

  const handleRefetch = async () => {
    return Promise.all([refetchGetTeam(), refetchGetTeamMembers()]);
  };

  const { getFlag } = useTypedFlags();
  const isLegacySiteEnabled = getFlag(LEGACY_SITE);

  const TabKeys = {
    currentMembers: 'current-members',
    formerMembers: 'former-members',
    pendingInvitations: 'pending-invitations',
    featureUsage: 'feature-usage',
    sso: 'sso-details',
    mcpSettings: 'mcp-settings',
    'data-feed-credit': 'data-feed-credit',
    'data-feeds-v2': 'data-feeds-v2',
    'external-api-clients': 'external-api-clients',
    'sf-opportunity': 'sf-opportunity',
    updates: 'updates',
  };

  const { defaultActiveKey, onChangeTab } = useSelectedTab({
    tabItems: Object.values(TabKeys),
  });

  const { onSubmit } = useUpdateTeam(refetchGetTeam);

  const hasEditAccess = useHasEditAccess();

  const hasAccessToDataFeedCredit = useHasAccess({
    pageId: dataFeedPermissions.pageId,
    permissionType: PermissionType.PUBLISH,
  });

  const hasAccessToExternalApiClients = useHasAccess({
    pageId: externalApiPermissions.pageId,
    permissionType: PermissionType.PUBLISH,
  });

  const {
    isPendingRenewal,
    hasChangePackagePermission,
    pauseSubscription,
    unpauseSubscription,
    cancelSubscription,
    isPausing,
    isUnpausing,
    isCancelling,
  } = usePendingRenewal(initialValues.isPendingRenewal ?? false);

  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [unpauseModalOpen, setUnpauseModalOpen] = useState(false);

  const onSaveTeamName = (name: string) => {
    onSubmit(
      {
        team_name: name,
      },
      initialValues.teamId
    )
      .then(() => {
        message.success("Success! The team's name has been updated.");
        refetchGetTeam();
      })
      .catch(() => {
        message.error(
          "Error! Failed to update the team's name. Please try again later."
        );
      });
  };

  const handlePauseConfirm = async () => {
    try {
      await pauseSubscription();
      await handleRefetch();
      message.success("The team's subscription has been paused.");
    } catch {
      message.error('Failed to pause the subscription. Please try again.');
    } finally {
      setPauseModalOpen(false);
    }
  };

  const handleUnpauseSubmit = async (
    packageId: number,
    packageStart: string,
    packageEnd: string
  ) => {
    try {
      await unpauseSubscription(packageId, packageStart, packageEnd);
      await handleRefetch();
      message.success("The team's subscription has been unpaused.");
    } catch {
      message.error('Failed to unpause the subscription. Please try again.');
    } finally {
      setUnpauseModalOpen(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await cancelSubscription();
      await handleRefetch();
      message.success("The team's subscription has been cancelled.");
    } catch {
      message.error('Failed to cancel the subscription. Please try again.');
    } finally {
      setUnpauseModalOpen(false);
    }
  };

  const { columns: pendingInvitationColumns, pendingInvitations } =
    useTeamInvitations({ idTeam });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  const teamNameValidation = (name: string) => {
    if (!name) {
      return 'Invalid input. Please enter a valid name.';
    }
    return '';
  };

  const tabItems: TabsProps['items'] = [
    {
      key: TabKeys.currentMembers,
      label: (
        <span className="text-xl">{`Team Members (${data?.length || 0})`}</span>
      ),
      children: (
        <TeamMembers
          columns={columns}
          data={data}
          idTeam={idTeam}
          onSetTeamMemberFilter={(value) => setTeamMemberFilter(value.trim())}
          isLoadingTeamMembers={isLoadingTeamMembers}
        />
      ),
    },
    {
      key: TabKeys.formerMembers,
      label: (
        <span className="text-xl">{`Former Members (${
          formerData?.length || 0
        })`}</span>
      ),
      children: <FormerTeamMembers columns={formerColumns} data={formerData} />,
    },
    {
      key: TabKeys.pendingInvitations,
      label: (
        <span className="text-xl">{`Invitations (${
          pendingInvitations?.length || 0
        })`}</span>
      ),
      children: (
        <PendingInvitations
          columns={pendingInvitationColumns}
          data={pendingInvitations}
        />
      ),
    },
    {
      key: TabKeys.featureUsage,
      label: <span className="text-xl">Feature Usage</span>,
      children: <FeatureUsage idTeam={idTeam} />,
    },
    {
      key: TabKeys.sso,
      label: <span className="text-xl">SSO Settings</span>,
      children: <SSODetails idTeam={idTeam} />,
    },
    {
      key: TabKeys.mcpSettings,
      label: <span className="text-xl">MCP Settings</span>,
      children: <MCPSettings idTeam={idTeam} />,
    },
    {
      key: TabKeys['data-feed-credit'],
      label: <span className="text-xl">Data Feed Credit</span>,
      children: (
        <DataFeedCredit
          idTeam={initialValues.teamId}
          name={initialValues.name}
        />
      ),
      disabled: !hasAccessToDataFeedCredit,
    },
    {
      key: TabKeys['data-feeds-v2'],
      label: <span className="text-xl">Data Feeds (v2)</span>,
      children: <DataFeedCreditV2 idTeam={initialValues.teamId} />,
      disabled: !hasAccessToDataFeedCredit,
    },
    {
      key: TabKeys['external-api-clients'],
      label: <span className="text-xl">External API Clients</span>,
      children: (
        <ExternalApiClients idTeam={idTeam} name={initialValues.name} />
      ),
      disabled: !hasAccessToExternalApiClients,
    },
    {
      key: TabKeys['sf-opportunity'],
      label: <span className="text-xl">SFDC Opportunity</span>,
      children: <SFOportunity accountId={initialValues.sfAccountId} />,
      disabled: !initialValues.sfAccountId,
    },
    {
      key: TabKeys.updates,
      label: <span className="text-xl">Updates</span>,
      children: <TeamUpdates idTeam={idTeam} />,
    },
  ];

  return (
    <>
      <Row>
        <Col xl={{ span: 12 }} md={{ span: 14 }}>
          <Row>
            <Col
              xl={{ push: 4 }}
              md={{ push: 3 }}
              xs={{ push: 1 }}
              className="mt-4"
            >
              <EditableText
                text={initialValues.name}
                onSave={onSaveTeamName}
                validation={teamNameValidation}
                ariaLabel="click to edit team name"
                render={(text) => (
                  <Typography.Title level={2}>{text}</Typography.Title>
                )}
              />
            </Col>
            <Col
              xl={{ push: 4 }}
              md={{ push: 3 }}
              xs={{ push: 1 }}
              className="flex items-center pl-4"
            >
              <Typography.Text>({initialValues.teamId})</Typography.Text>
            </Col>
          </Row>
          <Row>
            <Col xl={{ push: 4 }} md={{ push: 3 }} xs={{ push: 1 }}>
              <Breadcrumb
                routes={[
                  { breadcrumbName: 'Search', path: TeamRoutes.Search },
                  { breadcrumbName: `Team ${initialValues.teamId}`, path: '' },
                ]}
                itemRender={itemRender}
              />
            </Col>
          </Row>
        </Col>
        <Col xl={{ span: 12 }} md={{ span: 10 }}>
          <Row>
            <Col flex="auto" className="mt-4">
              <Row justify="end">
                <Col
                  xl={{ span: 12, pull: 4 }}
                  md={{ span: 20, pull: 5 }}
                  xs={{ span: 20, pull: 2 }}
                >
                  <TeamSearch />
                </Col>
              </Row>
            </Col>
          </Row>
          <Row justify="end">
            <Col className="mt-4 items-end" xl={{ pull: 4 }} md={{ pull: 5 }}>
              <Row>
                <Col>
                  <AddExistingUser
                    idTeam={idTeam}
                    refetchTeamMembers={refetchGetTeamMembers}
                    disabled={!hasEditAccess}
                  />
                </Col>
                <Col className="ml-2">
                  <Tooltip
                    title={
                      !hasChangePackagePermission
                        ? 'You do not have permission to pause or unpause this subscription'
                        : null
                    }
                  >
                    {isPendingRenewal ? (
                      <Button
                        type="primary"
                        onClick={() => setUnpauseModalOpen(true)}
                        disabled={!hasChangePackagePermission}
                      >
                        Unpause Subscription
                      </Button>
                    ) : (
                      initialValues.packageID !== EXPIRED_PACKAGE && (
                        <Button
                          danger
                          onClick={() => setPauseModalOpen(true)}
                          disabled={!hasChangePackagePermission}
                        >
                          Pause Subscription
                        </Button>
                      )
                    )}
                  </Tooltip>
                </Col>
                {isLegacySiteEnabled && (
                  <Col className="ml-2">
                    <Button
                      type="dashed"
                      target="_blank"
                      href={`${window.envVars.LEGACY_DOMAIN}/admin/team-edit/${idTeam}`}
                    >
                      Use Legacy Site
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      {isPendingRenewal && (
        <Row>
          <Col
            xl={{ push: 2, span: 20 }}
            md={{ push: 2, span: 20 }}
            xs={{ push: 1, span: 22 }}
          >
            <PendingRenewalBanner />
          </Col>
        </Row>
      )}
      <Row>
        <PackageSetting
          refetchTeam={handleRefetch}
          packageId={initialValues.packageID}
          packageName={initialValues.packageName}
          packageStart={initialValues.packageStart}
          packageEnd={initialValues.packageEnd}
          linkedEntity={parseInt(
            initialValues.linkedEntity as unknown as string,
            10
          )}
          idTeam={idTeam}
        />
        <TeamSettings
          domainWhitelist={initialValues.domainWhitelist}
          mfaStatus={initialValues.mfaStatus}
          lbsStatus={initialValues.lbsStatus}
          refetchTeam={handleRefetch}
          teamId={initialValues.teamId}
          teamType={initialValues.teamType}
          isHidden={initialValues.isHidden}
          memberCap={initialValues.seatLimit}
          sfAccountId={initialValues.sfAccountId}
        />
        <SFDCInfo
          recordedDate={initialValues.recordedDate}
          sfAccountId={initialValues.sfAccountId}
          teamId={initialValues.teamId}
          updateTeam={onSubmit}
          refetchGetTeam={refetchGetTeam}
        />
      </Row>
      <Row>
        <Col push="2" span="20">
          <Divider className="border-t-2 border-black border-solid" />
        </Col>
      </Row>
      <Row align="bottom">
        <Col
          xs={{ span: 22, push: 1 }}
          md={{ push: 2, span: 20 }}
          className="pb-20"
        >
          <Tabs
            defaultActiveKey={defaultActiveKey}
            items={tabItems}
            onChange={onChangeTab}
          />
        </Col>
      </Row>

      <PauseSubscriptionModal
        open={pauseModalOpen}
        isLoading={isPausing}
        onConfirm={handlePauseConfirm}
        onCancel={() => setPauseModalOpen(false)}
      />
      <UnpauseSubscriptionModal
        open={unpauseModalOpen}
        isLoading={isUnpausing}
        isCancelling={isCancelling}
        onSubmit={handleUnpauseSubmit}
        onCancelSubscription={handleCancelSubscription}
        onCancel={() => setUnpauseModalOpen(false)}
      />
    </>
  );
};
